/**
 * Workforce Hub W1 — URL semantics + legacy alias resolver.
 *
 * The People & Talent workspace today lives behind five URL faces:
 *   /employees
 *   /employees/profiles
 *   /employees/documents
 *   /employees/expiring
 *   /employees/profile/:managerId
 *
 * W1 introduces the canonical pair:
 *   /workforce                                  (with ?focus=...)
 *   /workforce/employees/:employeeProfileId     (with ?tab=...)
 *
 * Both URL families render the SAME workspace component. This module
 * is the single resolver: given any path, it returns
 *
 *   {
 *     isWorkforceRoute,
 *     focus,
 *     employeeProfileId,
 *     managerId,
 *     personIdentityType,
 *     canonicalPath,
 *     legacyPath
 *   }
 *
 * which App.js, the route table, and tests all consume so they stay in
 * lockstep. No App.js change can quietly break the legacy contract
 * without breaking these tests too.
 *
 * Important: this module does NOT redirect. It RESOLVES. Both legacy
 * and canonical URLs continue to resolve to the same workspace state.
 * Operators with bookmarks at /employees/expiring see exactly what an
 * operator landing at /workforce?focus=expiring sees. Sticky URL
 * parameters (loc, p, pt, yr, mo, kpi) are not touched.
 *
 * Future phases (W6) MAY swap aliases for hard redirects, at which
 * point this module becomes the redirect map. For W1 we keep both.
 */

const PERSON_WORKSPACE_BY_PROFILE_PATTERN = /^\/employees\/profile\/by-profile\/(\d+)$/;

/** Focus values the directory understands. */
export const WORKFORCE_FOCUS_VALUES = Object.freeze([
  'directory',
  'profiles',
  'documents',
  'expiring'
]);

/**
 * Legacy → canonical mapping for the four directory faces.
 * Used both for routing recognition and for telemetry-friendly
 * focus derivation.
 */
export const LEGACY_TO_CANONICAL_FOCUS = Object.freeze({
  '/employees': 'directory',
  '/employees/profiles': 'profiles',
  '/employees/documents': 'documents',
  '/employees/expiring': 'expiring'
});

const LEGACY_DIRECTORY_PATHS = new Set(Object.keys(LEGACY_TO_CANONICAL_FOCUS));
const LEGACY_PROFILE_PATTERN = /^\/employees\/profile\/(\d+)$/;
const CANONICAL_WORKFORCE_PATH = '/workforce';
const CANONICAL_EMPLOYEE_PATTERN = /^\/workforce\/employees\/(\d+)$/;

/**
 * Normalize a search string or URLSearchParams into a Map for cheap
 * repeated reads. Accepts a leading '?', no '?', or a URLSearchParams.
 */
function readSearchParams(search) {
  if (!search) return new URLSearchParams();
  if (search instanceof URLSearchParams) return search;
  const trimmed = String(search).startsWith('?') ? String(search).slice(1) : String(search);
  try {
    return new URLSearchParams(trimmed);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Coerce an arbitrary value into one of WORKFORCE_FOCUS_VALUES, or
 * return null when the value is missing / unknown. Unknown query
 * focus parameters resolve to the default workspace view (directory),
 * never to an error.
 */
function coerceFocus(value) {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  return WORKFORCE_FOCUS_VALUES.includes(normalized) ? normalized : null;
}

/**
 * Resolve a path + search into the canonical workforce workspace
 * state. Recognizes ALL of:
 *
 *   /employees
 *   /employees/profiles
 *   /employees/documents
 *   /employees/expiring
 *   /employees/profile/:id
 *   /workforce
 *   /workforce?focus=...
 *   /workforce/employees/:id
 *
 * Returns null when the path is NOT a workforce route. Callers use
 * the truthy return as the "render the workforce workspace" gate.
 */
export function resolveWorkforceRoute(path, search = '') {
  if (!path) return null;
  const params = readSearchParams(search);

  // ── Canonical: /workforce/employees/:id ─────────────────────────
  const canonicalEmployee = String(path).match(CANONICAL_EMPLOYEE_PATTERN);
  if (canonicalEmployee) {
    const employeeProfileId = Number(canonicalEmployee[1]);
    return {
      isWorkforceRoute: true,
      isCanonical: true,
      isLegacy: false,
      focus: 'directory',
      employeeProfileId: Number.isFinite(employeeProfileId) ? employeeProfileId : null,
      managerId: null,
      personIdentityType: Number.isFinite(employeeProfileId) ? 'employee_profile' : null,
      canonicalPath: `/workforce/employees/${employeeProfileId}`,
      legacyPath: `/employees/profile/by-profile/${employeeProfileId}`
    };
  }

  // ── Canonical: /workforce + optional ?focus ─────────────────────
  if (path === CANONICAL_WORKFORCE_PATH) {
    const focus = coerceFocus(params.get('focus')) || 'directory';
    return {
      isWorkforceRoute: true,
      isCanonical: true,
      isLegacy: false,
      focus,
      employeeProfileId: null,
      managerId: null,
      personIdentityType: null,
      canonicalPath: focus === 'directory'
        ? CANONICAL_WORKFORCE_PATH
        : `${CANONICAL_WORKFORCE_PATH}?focus=${focus}`,
      legacyPath: legacyPathForFocus(focus)
    };
  }

  // ── Legacy: /employees/profile/:id ──────────────────────────────
  const legacyProfile = String(path).match(LEGACY_PROFILE_PATTERN);
  if (legacyProfile) {
    const managerId = Number(legacyProfile[1]);
    return {
      isWorkforceRoute: true,
      isCanonical: false,
      isLegacy: true,
      focus: 'directory',
      employeeProfileId: null,
      managerId: Number.isFinite(managerId) ? managerId : null,
      personIdentityType: Number.isFinite(managerId) ? 'manager' : null,
      canonicalPath: null,
      legacyPath: `/employees/profile/${managerId}`
    };
  }

  const byProfileRoute = String(path).match(PERSON_WORKSPACE_BY_PROFILE_PATTERN);
  if (byProfileRoute) {
    const employeeProfileId = Number(byProfileRoute[1]);
    return {
      isWorkforceRoute: true,
      isCanonical: false,
      isLegacy: true,
      focus: 'directory',
      employeeProfileId: Number.isFinite(employeeProfileId) ? employeeProfileId : null,
      managerId: null,
      personIdentityType: Number.isFinite(employeeProfileId) ? 'employee_profile' : null,
      canonicalPath: `/workforce/employees/${employeeProfileId}`,
      legacyPath: `/employees/profile/by-profile/${employeeProfileId}`
    };
  }

  // ── Legacy: /employees, /employees/profiles, /documents, /expiring
  if (LEGACY_DIRECTORY_PATHS.has(path)) {
    const focus = LEGACY_TO_CANONICAL_FOCUS[path];
    return {
      isWorkforceRoute: true,
      isCanonical: false,
      isLegacy: true,
      focus,
      employeeProfileId: null,
      managerId: null,
      personIdentityType: null,
      canonicalPath: focus === 'directory'
        ? CANONICAL_WORKFORCE_PATH
        : `${CANONICAL_WORKFORCE_PATH}?focus=${focus}`,
      legacyPath: path
    };
  }

  return null;
}

/**
 * Inverse helper: given a focus value, return the legacy path it
 * maps to. Used by tests and by the navigation "quick links" group
 * that retains old labels for one release.
 */
export function legacyPathForFocus(focus) {
  switch (focus) {
    case 'profiles':  return '/employees/profiles';
    case 'documents': return '/employees/documents';
    case 'expiring':  return '/employees/expiring';
    case 'directory':
    default:          return '/employees';
  }
}

/** Convenience guards used by App.js + routeTable. */
export function isWorkforcePath(path) {
  const resolved = resolveWorkforceRoute(path);
  return Boolean(resolved?.isWorkforceRoute);
}

export function getPersonIdentityFromWorkforcePath(path) {
  const resolved = resolveWorkforceRoute(path);
  return {
    employeeProfileId: resolved?.employeeProfileId ?? null,
    managerId: resolved?.managerId ?? null,
    personIdentityType: resolved?.personIdentityType ?? null
  };
}

export function buildPersonWorkspacePath({
  employeeProfileId = null,
  managerId = null,
  tab = null
} = {}) {
  const numericProfileId = Number(employeeProfileId);
  const numericManagerId = Number(managerId);
  let path = '/employees';

  if (Number.isFinite(numericProfileId) && numericProfileId > 0) {
    path = `/employees/profile/by-profile/${numericProfileId}`;
  } else if (Number.isFinite(numericManagerId) && numericManagerId > 0) {
    path = `/employees/profile/${numericManagerId}`;
  }

  const normalizedTab = String(tab || '').trim().toLowerCase();
  if (!normalizedTab) return path;
  return `${path}?${new URLSearchParams({ tab: normalizedTab }).toString()}`;
}

export function isPersonWorkspacePath(path) {
  if (!path) return false;
  const normalizedPath = String(path || '');
  return PERSON_WORKSPACE_BY_PROFILE_PATTERN.test(normalizedPath)
    || /^\/employees\/profile\/\d+$/.test(normalizedPath)
    || /^\/workforce\/employees\/\d+$/.test(normalizedPath);
}

/**
 * Map a focus value to the telemetry subtype the analytics taxonomy
 * already supports. Keeps route.visit events comparable whether the
 * operator arrived via a legacy or canonical URL.
 */
export function focusToTelemetrySubtype(focus) {
  return WORKFORCE_FOCUS_VALUES.includes(focus) ? focus : 'directory';
}
